import * as THREE from "three";
import Experience from "./Experience";
import { toThreeVector } from "./Utils/aeCoords";

// Clip-based pivot distance, raw world units (verified against the
// reference's own sync code — NOT divided by 100, unlike most other preset
// "amt" fields). Smoothstepped across the clip boundary the playhead is
// currently crossing, so the pivot distance transitions smoothly rather
// than jumping at clip edges.
const PIVOT_BY_CLIP = {
  sky: 600,
  dolly: 600,
  explode: 200,
};

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function smoothstep01(t) {
  return t * t * (3 - 2 * t);
}

// Scratch vectors reused by applyToCamera() every tick instead of
// allocating fresh Vector3s (.clone()/.sub()) each call.
const _pos = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _up = new THREE.Vector3();

export default class CameraPath {
  constructor() {
    this.experience = new Experience();
    this.sceneData = this.experience.sceneData;

    this.bakedQuat = new THREE.Quaternion();

    this.setFrames();
  }

  // Precompute every keyframe's AE points as real Vector3s once, rather
  // than converting on every applyToCamera() call.
  setFrames() {
    const { comp, camera } = this.sceneData.scene;
    this.compWidth = comp.width;
    this.compHeight = comp.height;
    this.firstFrame = comp.startFrame;
    this.lastFrame = comp.endFrame;

    this.frames = camera.frames.map((frame) => ({
      p: toThreeVector(frame.p, this.compWidth, this.compHeight),
      f: toThreeVector(frame.f, this.compWidth, this.compHeight),
      u: toThreeVector(frame.u, this.compWidth, this.compHeight),
      zoom: frame.zoom,
    }));
  }

  // Sets cameraInstance's position/up/lookAt/fov for the given timeline
  // frame, interpolating between the two nearest baked keyframes. Captures
  // and returns the resulting quaternion BEFORE tilt is applied on top —
  // other systems (turntable's unpin mode) need this untilted orientation.
  applyToCamera(cameraInstance, playheadFrame) {
    const rel = THREE.MathUtils.clamp(
      playheadFrame - this.firstFrame,
      0,
      this.frames.length - 1,
    );

    const i0 = Math.floor(rel);
    const i1 = Math.min(i0 + 1, this.frames.length - 1);
    const t = rel - i0;

    const A = this.frames[i0];
    const B = this.frames[i1];

    const pos = _pos.copy(A.p).lerp(B.p, t);
    const fwd = _fwd.copy(A.f).lerp(B.f, t);
    const up = _up.copy(A.u).lerp(B.u, t);
    const zoom = lerp(A.zoom, B.zoom, t);

    cameraInstance.position.copy(pos);
    cameraInstance.up.copy(up.sub(pos).normalize());
    cameraInstance.lookAt(fwd);
    cameraInstance.fov =
      (2 * Math.atan(this.compHeight / (2 * zoom)) * 180) / Math.PI;
    cameraInstance.updateProjectionMatrix();

    this.bakedQuat.copy(cameraInstance.quaternion);

    return this.bakedQuat;
  }

  // Distance from the camera to its tilt pivot at the given timeline frame,
  // smoothstepped across whichever clip boundary the frame is crossing.
  getPivotDistance(playheadFrame) {
    const clips = this.sceneData.clips;
    const clip = this.sceneData.getClipAt(playheadFrame);
    const clipIndex = clips.indexOf(clip);
    const nextClip = clips[clipIndex + 1];

    const v0 = PIVOT_BY_CLIP[clip.id];
    if (!nextClip) return v0;

    const t = THREE.MathUtils.clamp(
      (playheadFrame - clip.from) / Math.max(1, clip.to - clip.from),
      0,
      1,
    );
    const v1 = PIVOT_BY_CLIP[nextClip.id];
    return v0 + (v1 - v0) * smoothstep01(t);
  }
}
